import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import type { UnitRankingJadlog } from "@/hooks/useImpactDataJadlog";
interface ImpactByUnitChartJadlogProps {
  data: UnitRankingJadlog[];
}
const BAR_COLORS_JADLOG = [
  "#15803D",
  "#22C55E",
  "#4ADE80",
  "#86EFAC",
  "#D1FAE5",
];
export const ImpactByUnitChartJadlog = ({
  data,
}: ImpactByUnitChartJadlogProps) => {
  const sortedData = [...data].sort((a, b) => b.value - a.value).slice(0, 5);
  const formatXAxis = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground">
            {payload[0].payload.unit}
          </p>
          <p className="text-sm text-green-600">
            Massa: {payload[0].value.toLocaleString("pt-BR")} kg
          </p>
          <p className="text-sm text-emerald-600">
            CO₂ evitado: {payload[0].payload.co2.toLocaleString("pt-BR")} kg
          </p>
        </div>
      );
    }
    return null;
  };
  if (sortedData.length === 0) {
    return null;
  }
  return (
    <Card className="bg-card shadow-md hover:shadow-lg transition-shadow">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-[#10261F]">
          Ranking por Resíduos
        </CardTitle>
        <CardDescription className="text-gray-600">
          Resíduos com maior impacto ambiental positivo
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sortedData}
              layout="vertical"
              margin={{
                top: 0,
                right: 20,
                left: 10,
                bottom: 0,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                horizontal={false}
              />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tickFormatter={formatXAxis}
                tick={{
                  fill: "var(--muted-foreground)",
                  fontSize: 12,
                }}
              />
              <YAxis
                type="category"
                dataKey="unit"
                axisLine={false}
                tickLine={false}
                tick={(props: any) => {
                  const { x, y, payload } = props;
                  const maxLength = 12;
                  const text =
                    payload.value.length > maxLength
                      ? `${payload.value.substring(0, maxLength)}...`
                      : payload.value;
                  return (
                    <text
                      x={x}
                      y={y}
                      dy={4}
                      textAnchor="end"
                      fill="var(--muted-foreground)"
                      fontSize={12}
                    >
                      {text}
                    </text>
                  );
                }}
                width={100}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  fill: "var(--muted)",
                }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {sortedData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      BAR_COLORS_JADLOG[index] ||
                      BAR_COLORS_JADLOG[BAR_COLORS_JADLOG.length - 1]
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
